/**
 * Single Source of Truth for Authentication Error Mappings
 * 
 * This configuration is used by:
 * - authErrorHandler.ts (middleware)
 * - loginErrorMapper.ts (form error handling)
 * 
 * Synced with backend error codes from:
 * - flow-server/src/common/exceptions/codes/error-codes.enum.ts
 * - flow-server/src/modules/auth/auth.service.ts
 */

export interface AuthErrorMapping {
  messageKey: string;
  fieldKey?: string;
}

/**
 * Authentication Error Code to i18n Key Mapping
 * 
 * Rules:
 * - If fieldKey is present: Error shown under specific form field
 * - If fieldKey is absent: Error shown in Snackbar
 */
export const AUTH_ERROR_CODE_MAP: Record<string, AuthErrorMapping> = {
  // ========================================
  // AUTH Domain - User Errors
  // ========================================
  
  USER_NOT_FOUND: {
    messageKey: 'login.invalid-credentials', // Security: Don't reveal if user exists
  },
  
  USER_INACTIVE: {
    messageKey: 'login.user-inactive',
  },
  
  INVALID_CREDENTIALS: {
    messageKey: 'login.invalid-credentials', // Security: Generic message
  },
  
  // ========================================
  // AUTH Domain - Company Errors
  // ========================================
  
  AUTHENTICATION_FAILED: {
    messageKey: 'login.invalid-credentials', // Default, can be overridden by details
  },
  
  COMPANY_INACTIVE: {
    messageKey: 'login.company-is-not-active',
  },

  INSUFFICIENT_PERMISSIONS: {
    messageKey: 'login.role-not-assigned',
  },
  
  // ========================================
  // AUTH Domain - Token Errors
  // ========================================
  
  TOKEN_EXPIRED: {
    messageKey: 'login.token-expired',
  },
  
  TOKEN_INVALID: {
    messageKey: 'login.invalid-token',
  },
  
  // ========================================
  // CLIENT Domain
  // ========================================
  
  CLIENT_NOT_FOUND_OR_INACTIVE: {
    messageKey: 'login.client-not-found',
    fieldKey: 'phone',
  },

  CLIENT_USER_DEACTIVATED: {
    messageKey: 'login.user-deactivated',
    fieldKey: 'phone',
  },
  
  // ========================================
  // SYSTEM Domain
  // ========================================
  
  INTERNAL_ERROR: {
    messageKey: 'login.internal-error',
  },
};

/**
 * Special handling for errors with details field
 * These override the default mapping based on the details value
 */
export const AUTH_ERROR_DETAILS_MAP: Record<string, Record<string, AuthErrorMapping>> = {
  AUTHENTICATION_FAILED: {
    'company-not-ready': {
      messageKey: 'login.company-not-ready',
    },
    'company-not-ready-yet': {
      messageKey: 'login.company-not-ready',
    },
    'User has no valid accounts': {
      messageKey: 'login.no-valid-accounts',
      fieldKey: 'phone',
    },
  },
  
  INVALID_CREDENTIALS: {
    'invalid-password-provided': {
      messageKey: 'login.invalid-credentials',
      fieldKey: 'password',
    },
  },
  
  TOKEN_INVALID: {
    'invitation-already-used': {
      messageKey: 'invitation-already-used',
    },
    'invitation-expired': {
      messageKey: 'invitation-expired',
    },
    'invitation-not-found': {
      messageKey: 'invitation-not-found',
    },
  },
};

/**
 * Get error mapping for a given error code and optional details
 * 
 * @param code - Error code from backend (e.g., 'USER_NOT_FOUND')
 * @param details - Optional details string from backend
 * @returns AuthErrorMapping with messageKey and optional fieldKey
 */
export function getAuthErrorMapping(code: string, details?: string): AuthErrorMapping | null {
  // Check if there's a details-specific mapping
  if (details && AUTH_ERROR_DETAILS_MAP[code]) {
    const detailsMap = AUTH_ERROR_DETAILS_MAP[code];
    
    // Check for exact match
    if (detailsMap[details]) {
      return detailsMap[details];
    }
    
    // Check for partial match (e.g., details.includes('company-not-ready'))
    for (const [detailKey, mapping] of Object.entries(detailsMap)) {
      if (details.includes(detailKey)) {
        return mapping;
      }
    }
  }
  
  // Return default mapping for the code
  return AUTH_ERROR_CODE_MAP[code] || null;
}

